import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';

interface CustomElementOptions {
  tagName: string;
  elementClass?: typeof HTMLElement;
  observedAttributes?: string[];
  cssString?: string;
}

type ReactComponentType = React.ComponentType<any>;

/**
 * Converts kebab-case to camelCase
 */
const kebabToCamel = (str: string): string => {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Registers a React component as a Custom Element
 */
export function registerReactComponentAsCustomElement(
  Component: ReactComponentType,
  options: CustomElementOptions
): void {
  const { tagName, elementClass = HTMLElement, observedAttributes = [], cssString = '' } = options;

  // Create a custom element class
  class ReactCustomElement extends elementClass {
    private reactRoot: ReactDOM.Root | null = null;
    private props: Record<string, any> = {};
    // Add _bannerRef property to store the external ref
    private _bannerRef: any = null;
    private container: HTMLDivElement | null = null;

    constructor() {
      super();
      
      // Add styles directly to the document head since we're not using Shadow DOM
      const style = document.createElement('style');
      
      // Define styles for the custom element without Shadow DOM
      style.textContent = `
        ${tagName} {
          display: block !important;
          width: 100% !important;
          height: 100% !important;
          box-sizing: border-box !important;
          left: 0;
          /* PlazaWeb CSS Variables */
          --primary-color: var(--plaza-primary, #0052CC);
          --secondary-color: var(--plaza-secondary, #00D084);
          --accent-color: var(--plaza-color-error, #E74C3C);
          --text-color: var(--plaza-gray-900, #111827);
          --light-text: var(--plaza-gray-50, #F9FAFB);
          --background-color: var(--plaza-gray-50, #F9FAFB);
          --border-color: var(--plaza-gray-200, #E5E7EB);
          --color-text: var(--plaza-gray-50, #F9FAFB);
          --color-text-rgb: 249, 250, 251;
          --color-error: #E74C3C;
          
          /* UI-specific variables */
          --glass-bg: var(--plaza-glass-dark, rgb(26 56 99 / 57%));
          --glass-border: var(--plaza-glass-border, rgb(26 56 99 / 40%));
          --shadow-color: rgba(0, 0, 0, 0.3);
          --font-family-text: var(--plaza-font-family, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif);
          --font-family-headlines: var(--plaza-font-family, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif);
          --border-radius-full: 9999px;
          --border-radius-large: 16px;
          --element-padding: 12px 18px;
          --font-size-base: 1rem;
          --font-size-small: 0.9rem;
          --font-size-large: 1.2rem;
          
          /* UI layer specific variables */
          --primary-color-ui: var(--plaza-primary, #0052CC);
          --secondary-color-ui: var(--plaza-secondary, #00D084);
          --text-color-ui: var(--plaza-gray-50, #F9FAFB);
          --glass-bg-ui: rgb(31 31 31 / 57%);
          --glass-border-ui: rgb(26 56 99 / 40%);
          --shadow-color-ui: rgba(0, 0, 0, 0.3);
        }
        
        ${tagName} .react-container {
          width: 100% !important;
          height: 100% !important;
          position: relative !important;
          overflow: hidden !important;
          isolation: isolate !important;
        }
        
        /* Inject component-specific CSS */
        ${cssString}
      `;
      
      // Check if style is already added to avoid duplicates
      if (!document.querySelector(`style[data-plaza-element="${tagName}"]`)) {
        style.setAttribute('data-plaza-element', tagName);
        document.head.appendChild(style);
      }
    }

    // Lifecycle: when element is added to the DOM
    connectedCallback() {
      try {
        // Create the container when the element is connected to the DOM
        if (!this.container) {
          this.container = document.createElement('div');
          this.container.className = 'react-container';
          this.appendChild(this.container);
          
          // Create a React root
          this.reactRoot = ReactDOM.createRoot(this.container);
        }
        
        // Check for the _bannerRef property that would be set from createControlledBanner
        if ((this as any)._bannerRef) {
          this._bannerRef = (this as any)._bannerRef;
          this.props.triggerRef = this._bannerRef;
        }
        
        this.updateComponent();
      } catch (error) {
        console.error('Custom element connectedCallback error:', error);
        Sentry.captureException(error instanceof Error ? error : new Error('Custom element connection error'), {
          extra: {
            tagName,
            hasContainer: !!this.container,
            hasReactRoot: !!this.reactRoot,
            props: this.props
          },
          tags: {
            component: 'custom_element',
            operation: 'connected_callback'
          }
        });
      }
    }

    // Lifecycle: when element is removed from the DOM
    disconnectedCallback() {
      try {
        if (this.reactRoot) {
          this.reactRoot.unmount();
          this.reactRoot = null;
        }
      } catch (error) {
        console.error('Custom element disconnectedCallback error:', error);
        Sentry.captureException(error instanceof Error ? error : new Error('Custom element disconnection error'), {
          extra: {
            tagName,
            hasReactRoot: !!this.reactRoot
          },
          tags: {
            component: 'custom_element',
            operation: 'disconnected_callback'
          }
        });
      }
    }

    // Lifecycle: when attributes change
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
      if (oldValue !== newValue) {
        // Convert attribute name to camelCase for React props
        const propName = kebabToCamel(name);
        
        // Special handling for trigger-ref attribute
        if (name === 'trigger-ref' && newValue === 'true') {
          // This is just a signal that we should use the _bannerRef property
          if ((this as any)._bannerRef) {
            this._bannerRef = (this as any)._bannerRef;
            this.props.triggerRef = this._bannerRef;
          }
        } else {
          // Try to parse the attribute value as JSON if possible
          let parsedValue = newValue;
          try {
            parsedValue = JSON.parse(newValue);
          } catch {
            // If it's not valid JSON, keep the string value
          }
          
          this.props[propName] = parsedValue;
        }
        
        this.updateComponent();
      }
    }

    // Update React component with current props
    private updateComponent() {
      try {
        if (this.reactRoot && this.container) {
          this.reactRoot.render(React.createElement(Component, this.props));
        }
      } catch (error) {
        console.error('Custom element updateComponent error:', error);
        Sentry.captureException(error instanceof Error ? error : new Error('Custom element update error'), {
          extra: {
            tagName,
            hasReactRoot: !!this.reactRoot,
            hasContainer: !!this.container,
            props: this.props,
            componentName: Component.name || 'Unknown'
          },
          tags: {
            component: 'custom_element',
            operation: 'update_component'
          }
        });
      }
    }

    // Getter for observed attributes (required for attributeChangedCallback to work)
    static get observedAttributes() {
      return [...observedAttributes, 'trigger-ref'];
    }
  }

  // Register the custom element
  try {
    if (!customElements.get(tagName)) {
      customElements.define(tagName, ReactCustomElement);
      console.log(`Custom element '${tagName}' registered successfully`);
    } else {
      console.warn(`Custom element '${tagName}' already registered`);
    }
  } catch (error) {
    console.error(`Failed to register custom element '${tagName}':`, error);
    Sentry.captureException(error instanceof Error ? error : new Error('Custom element registration error'), {
      extra: {
        tagName,
        componentName: Component.name || 'Unknown',
        observedAttributes,
        isAlreadyDefined: !!customElements.get(tagName)
      },
      tags: {
        component: 'custom_element',
        operation: 'register_element'
      }
    });
    throw error;
  }
} 